/**
 * Componente EntryAgGridTable
 *
 * Wrapper sobre AgGridTable para la lista de Entries.
 * Maneja la configuración específica de columnas, sorting y acciones.
 */

import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { ColDef } from 'ag-grid-community';
import React, { useCallback, useRef } from 'react';
import { Entry } from '../../../models/Entry';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';

interface EntryAgGridTableProps {
  entries: Entry[];
  isLoading?: boolean;
  isFetching?: boolean;
  className?: string;
  selectedColumns?: string[];
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
  onCancelEntry?: (entry: Entry) => void;
  canCancel?: boolean;
  isTenant3pl?: boolean;
}

export const EntryAgGridTable = React.memo(function EntryAgGridTable({
  entries,
  isFetching = false,
  selectedColumns = [],
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  sorting,
  onSortingChange,
  onCancelEntry,
  canCancel = false,
  isTenant3pl = false,
}: EntryAgGridTableProps) {
  const agGridRef = useRef<AgGridTableRef>(null);

  // Usar el hook para manejar las columnas
  const { filteredColumnDefs } = useAgGridColumns({
    selectedColumns,
    onCancelEntry,
    canCancel,
    isTenant3pl,
  });

  // Handler no-op para onGridReady
  const handleGridReady = useCallback(() => {}, []);

  // Las columnas ya vienen con sus cellRenderers desde el hook
  const columnDefsWithRenderers: ColDef[] = filteredColumnDefs;

  return (
    <div
      className={`flex min-h-0 w-full flex-1 flex-col ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <AgGridTable
        ref={agGridRef}
        data={entries}
        columnDefs={columnDefsWithRenderers}
        height="100%"
        enableSelection={false}
        onGridReady={handleGridReady}
        className="min-h-0 flex-1 rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        gridOptions={{
          suppressColumnVirtualisation: true,
          suppressRowVirtualisation: false,
        }}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
});
